// aifab - useApi hook
//
// Reactive GET fetch with in-flight deduplication.
//
// Two problems this solves:
//   1. The same URL mounted in multiple components fires N parallel requests.
//      useApi now coalesces identical in-flight requests into one.
//   2. Very-short-lived duplicate calls (e.g. strict-mode double-mount) are
//      also collapsed without a shared cache that would require manual invalidation.
//
// Design: _inflight is a module-level Map<url, Promise<any>>.
// Any caller that arrives while a request is in-flight gets the same Promise.
// The entry is cleared when the Promise settles — there is no TTL or stale
// data concern, because reload() always fires a fresh fetch.
//
// On 401 the apiFetch layer fires aifab:logout and App.jsx redirects.
// SESSION_EXPIRED errors are suppressed here so no stale error state
// leaks into the UI before the redirect completes.

import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from './apiFetch';

// Module-level in-flight dedup map — shared across all useApi instances
const _inflight = new Map();

function fetchDeduped(path) {
  if (_inflight.has(path)) {
    return _inflight.get(path);   // coalesce: return the existing promise
  }
  const promise = apiFetch(path).finally(() => _inflight.delete(path));
  _inflight.set(path, promise);
  return promise;
}

export function useApi(path, deps = []) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(!!path);
  const [error,   setError]   = useState(null);

  const reload = useCallback(() => {
    if (!path) { setLoading(false); return; }
    // reload() always bypasses the dedup map so callers get fresh data
    setLoading(true);
    setError(null);
    apiFetch(path)
      .then(d => { setData(d); setError(null); })
      .catch(e => {
        if (e.code === 'SESSION_EXPIRED') return;
        setError(e.message);
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...(deps || [])]);

  useEffect(() => {
    if (!path) { setLoading(false); return; }
    // Initial mount uses deduplication
    setLoading(true);
    setError(null);
    fetchDeduped(path)
      .then(d => { setData(d); setError(null); })
      .catch(e => {
        if (e.code === 'SESSION_EXPIRED') return;
        setError(e.message);
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...(deps || [])]);

  return { data, loading, error, reload };
}
